import {
  createContext,
  createElement,
  ReactNode,
  useContext,
  useEffect,
} from "react"

const DocumentTitleTrackingContext = createContext<
  ((title: string) => void) | null
>(null)

export const DocumentTitleTrackingProvider = ({
  children,
  onTitleReady,
}: {
  children: ReactNode
  onTitleReady: (title: string) => void
}) =>
  createElement(
    DocumentTitleTrackingContext.Provider,
    { value: onTitleReady },
    children
  )

/**
 * Sets the document title with a consistent suffix and restores the previous title on unmount
 * @param title The main title to display
 * @param options.raw when true, use `title` verbatim without appending the "· Zano" suffix
 * @param options.ready when false, update the browser title without reporting it as analytics-ready
 */
export function useDocumentTitle(
  title: string,
  options?: { raw?: boolean; ready?: boolean }
) {
  const raw = options?.raw ?? false
  const ready = options?.ready ?? true
  const onTitleReady = useContext(DocumentTitleTrackingContext)

  useEffect(() => {
    const previousTitle = document.title
    document.title = raw ? title : `${title} · Zano`
    if (ready) onTitleReady?.(document.title)

    return () => {
      document.title = previousTitle
    }
  }, [title, raw, ready, onTitleReady])
}
